import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getSharedLang, persistSharedLang } from '@easydent/shared-ui';
// Feature copy lives in its own modules — it is the bulk of the site's text and would otherwise
// double this file.
import { featuresEn } from '@/i18n/features.en';
import { featuresRo } from '@/i18n/features.ro';

// Public directory is Romania-first: default to Romanian, with English available via the toggle.
// A language carried from another app (via ?lang= or the shared cookie) wins over the local default.
const shared = getSharedLang();
const saved = shared ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('lang') : null);

const resources = {
  ro: {
    translation: {
      demo: {
        roles: {
          admin: "Administrator",
          doctor: "Doctor",
          receptionist: "Recepționer",
          adminDesc: "Administrare completă a clinicii",
          doctorDesc: "Clinic & calendar",
          receptionistDesc: "Recepție & programări",
        },
      },
      features: featuresRo,
      lang: { ro: 'RO', en: 'EN' },
      common: {
        language: 'Limbă',
        theme: { label: 'Temă', light: 'Luminoasă', dark: 'Întunecată', system: 'Sistem' },
        back: 'Înapoi', loading: 'Se încarcă…', backToDirectory: '← Înapoi la director',
        cookieBanner: {
          dialogLabel: 'Consimțământ cookie-uri',
          intro: 'Folosim cookie-uri strict necesare pentru funcționare. Cu acordul tău, folosim și cookie-uri de analiză și de marketing.',
          policyLink: 'Politica de cookie-uri',
          necessary: 'Strict necesare (mereu active)',
          analytics: 'Analiză',
          marketing: 'Marketing',
          acceptAll: 'Accept toate',
          necessaryOnly: 'Doar necesare',
          customise: 'Personalizează',
          saveChoice: 'Salvează alegerea',
        },
      },
      imaging: {
        title: 'Pentru centrele de imagistică dentară',
        subtitle: 'Preia trimiteri de la cabinetele cu care lucrezi deja și trimite imaginile direct în dosarele pacienților lor.',
        step1: 'Sosește o trimitere',
        step1Body: 'Un medic îți trimite un pacient, iar solicitarea ajunge în lista ta de lucru — cine vine, ce incidențe, ce dinți și de ce.',
        step2: 'Faci investigația',
        step2Body: 'Accepți sau refuzi cu un motiv. Dacă pacientul nu se prezintă, marchează asta — cabinetul trebuie să știe.',
        step3: 'Investigația se întoarce direct',
        step3Body: 'Încarci imaginile și apar în dosarul pacientului și în consultația din care au fost cerute. Fără CD-uri, fără WeTransfer.',
        priceTitle: 'Înscriere gratuită',
        priceBody: 'Nu se percepe nimic pentru primirea trimiterilor sau livrarea investigațiilor. Dacă asta se va schimba vreodată, vei fi anunțat din timp.',
        howToJoin: 'Conturile sunt create de easyDent, ca să putem verifica centrul înainte ca vreo dată despre pacienți să ajungă la el. Scrie-ne și te înrolăm.',
        signIn: 'Autentificare',
        tryDemo: 'Deschide demo-ul',
      },
      patients: {
        title: 'Pentru pacienți',
        subtitle: 'Istoricul tău stomatologic îți aparține. Portalul este gratuit, iar cabinetul tău te invită în el.',
        recordsTitle: 'Dosarul tău, oriunde ai fost tratat',
        recordsBody: 'Tratamente, schemă dentară, radiografii și documente de la fiecare cabinet conectat — într-un singur loc și ale tale, descărcabile oricând.',
        bookingTitle: 'Programează-te fără telefon',
        bookingBody: 'Vezi intervalele libere la cabinetul tău și îți faci, muți sau anulezi singur programarea. Reamintirile vin pe canalul ales de tine.',
        payTitle: 'Plătește și păstrează chitanțele',
        payBody: 'Achiți ce ai de plată din telefon și păstrezi fiecare chitanță și factură într-o singură listă.',
        controlTitle: 'Tu decizi cine ce vede',
        controlBody: 'Conectarea unui cabinet nu îi dă istoricul tău de la alte cabinete — partajarea este o alegere separată și o poți retrage oricând. Nimic nu este vândut nimănui, niciodată.',
        howToJoin: 'Fără formular de înscriere: cabinetul îți trimite un link de invitație, așa că un cont există doar pentru cineva care chiar este pacientul lor.',
        findClinic: 'Găsește un cabinet',
        signIn: 'Autentificare',
        tryDemo: 'Deschide demo-ul',
      },
      suppliers: {
        title: 'Pentru furnizorii din stomatologie',
        subtitle: 'Ajungi la medici și la pacienții lor chiar în programul pe care cabinetul îl are deja deschis — nu într-un inbox.',
        step1: 'Încarci materialul',
        step1Body: 'O imagine și un link. Îl verificăm înainte să ajungă undeva — cabinetele văd doar materiale aprobate.',
        step2: 'Alegi cine îl vede',
        step2Body: 'Personalul cabinetului, în aplicația în care lucrează toată ziua, sau pacienții, în portalul lor. Stabilești un buget și un interval de date.',
        step3: 'Plătești doar ce a fost chiar văzut',
        step3Body: 'O afișare contează abia după o secundă întreagă pe ecran. Cheltuiala se scade dintr-un sold alimentat în avans, deci nu ai facturi de urmărit și nu poți depăși bugetul.',
        priceTitle: '6 € la mia de afișări',
        priceBody: 'Se taxează dintr-un sold pe care îl alimentezi în avans. Nimic nu rulează când soldul este gol și poți opri o campanie oricând.',
        privacyTitle: 'Ce nu primești',
        privacyBody: 'Niciodată date despre pacienți. Vezi afișări și clicuri agregat — niciodată cine ți-a văzut reclama. Plasările către pacienți necesită în plus consimțământul de marketing al pacientului.',
        howToJoin: 'Conturile sunt create de easyDent, ca să putem verifica furnizorul înainte ca ceva de-al lui să fie arătat unui cabinet. Scrie-ne și te înrolăm.',
        signIn: 'Autentificare',
        tryDemo: 'Deschide demo-ul',
      },
      home: {
        nav: { findDentist: 'Găsește un stomatolog', forClinics: 'Pentru cabinete', features: 'Funcționalități', signIn: 'Autentificare', staff: 'Personal cabinet', patient: 'Pacient', menu: 'Meniu' },
        hero: {
          title: 'Găsește un stomatolog și programează-te online',
          subtitle: 'Caută cabinete stomatologice din apropiere, compară serviciile și programează-te în câteva secunde — fără cont.',
          keyword: 'Cabinet, medic sau tratament',
          city: 'Oraș',
          search: 'Caută',
          browseAll: 'Vezi toate cabinetele →',
          trust: 'Gratuit pentru pacienți · Fără cont · Conform GDPR',
        },
        steps: {
          title: 'Programează-te în trei pași',
          s1t: 'Caută', s1d: 'Găsește cabinete după nume, oraș sau tratament.',
          s2t: 'Alege', s2d: 'Compară medici, servicii și program.',
          s3t: 'Programează', s3d: 'Alege o oră și confirmă online — primești o reamintire.',
        },
        features: {
          booking: 'Programări online 24/7', bookingD: 'Rezervă o oră oricând, zi sau noapte.',
          reminders: 'Reamintiri automate', remindersD: 'SMS, WhatsApp sau email ca să nu uiți.',
          records: 'Datele tale, online', recordsD: 'Programări, istoric și plăți într-un singur loc.',
          privacy: 'Confidențial din construcție', privacyD: 'Datele tale sunt criptate și protejate GDPR.',
        },
        clinics: {
          kicker: 'Pentru cabinete stomatologice',
          title: 'Gestionează tot cabinetul în easyDent',
          subtitle: 'Programări, fișe pacienți, harta dentară, plăți și comunicare cu pacienții — o platformă cloud, fără instalări.',
          f1: 'Calendar inteligent cu predicția neprezentărilor',
          f2: 'Hartă dentară digitală și fișe clinice',
          f3: 'Plăți online și reamintiri automate',
          f4: 'Cabinetul tău listat în directorul de pacienți',
          cta: 'Începe proba gratuită de 14 zile',
          ctaNote: 'Fără card bancar.',
          learn: 'Vezi cum funcționează →',
        },
        demo: {
          title: 'Doar explorezi?',
          text: 'Intră într-un cabinet demo complet populat — fără cont. Datele se resetează zilnic.',
          staff: 'Personal cabinet', patient: 'Portal pacient', enter: 'Intră în demo →',
          note: 'Mediu de test partajat pentru evaluare.',
            imaging: 'Centru imagistică',
            imagingDesc: 'Trimiteri primite, investigații returnate',
            patientDesc: 'Programări, dosar, plăți',
            supplier: 'Furnizor',
            supplierDesc: 'Campanii, materiale, cheltuieli',
        },
        footer: {
          features: 'Toate funcționalitățile',
          tagline: 'Platforma pentru cabinete stomatologice.',
          patients: 'Pacienți', clinics: 'Cabinete', account: 'Autentificare',
          findDentist: 'Găsește un stomatolog', forClinics: 'Pentru cabinete', freeTrial: 'Probă gratuită',
          clinicSignin: 'Autentificare cabinet', patientSignin: 'Autentificare pacient',
          rights: '© 2026 easyDent. Toate drepturile rezervate.',
          legal: 'Legal',
        },
      },
      contact: {
        title: 'Contact',
        seoDescription: 'Scrie-ne despre easyDent — cabinete, furnizori, centre de imagistică și pacienți.',
        intro: 'Scrie-ne și îți răspundem, de obicei într-o zi lucrătoare. Dacă ai deja cont de cabinet, o solicitare de suport din aplicație ajunge mai repede la echipa potrivită.',
        name: 'Nume',
        email: 'Email',
        phone: 'Telefon',
        optional: '(opțional)',
        topic: 'Despre ce este vorba',
        topics: {
          clinics: 'Sunt un cabinet stomatologic',
          suppliers: 'Sunt furnizor',
          imaging: 'Sunt centru de imagistică',
          patients: 'Sunt pacient',
          billing: 'Facturare și abonament',
          other: 'Altceva',
        },
        message: 'Mesaj',
        messagePlaceholder: 'Spune-ne cu ce te putem ajuta.',
        privacy: 'Folosim datele de mai sus doar ca să îți răspundem. Nu îți trimitem materiale de marketing pe baza acestui mesaj.',
        submit: 'Trimite mesajul',
        sending: 'Se trimite…',
        doneTitle: 'Mesajul a ajuns la noi',
        doneBody: 'Ți-am trimis și o confirmare pe email. Îți răspundem în scurt timp — dacă vrei să adaugi ceva, scrie-ne la contact@easydent365.com.',
        error: 'Mesajul nu a putut fi trimis. Încearcă din nou în câteva momente.',
        rateLimited: 'Am primit deja câteva mesaje de la tine. Așteaptă puțin înainte să trimiți altul.',
      },
      legal: {
        title: 'Documente legale',
        seoDescription: 'Termeni și condiții, politica de confidențialitate și politica de cookie-uri pentru easyDent.',
        notFound: 'Acest document nu există.',
        loadError: 'Documentul nu a putut fi încărcat. Încercați din nou în scurt timp.',
        draft: 'Ciornă — în curs de avizare juridică.',
        version: 'Versiunea {{version}} · în vigoare din {{date}}',
        doc: {
          terms: 'Termeni și condiții',
          privacy: 'Politica de confidențialitate',
          cookies: 'Politica de cookie-uri',
          dpa: 'Acord de prelucrare a datelor',
          'patient-privacy': 'Notă de informare pacienți',
          'sub-processors': 'Sub-împuterniciți',
        },
      },
      landing: {
        tagline: 'Platforma pentru cabinete stomatologice — alege o aplicație.',
        findDentistTitle: 'Cauți un stomatolog?',
        findDentistText: 'Găsește cabinete stomatologice pe hartă și programează-te online.',
        findClinic: 'Găsește un cabinet →',
        demoTitle: 'Intră în cabinetul demo',
        demoText: 'Un mediu de test complet populat — fără cont. Alege un rol pentru a intra în aplicația clinicii:',
        enterDemo: 'Intră în demo →',
        staffLabel: 'Personal cabinet',
        patientLabel: 'Portal pacient',
        patientDesc: 'Programări, istoric medical și plăți',
        manualSignin: 'Autentificarea manuală folosește aceleași emailuri cu parola',
        sharedSandbox: 'Mediu de test partajat — datele se resetează zilnic.',
        applications: 'Aplicații',
        comingSoon: 'În curând',
        open: 'Deschide →',
      },
      directory: {
        title: 'Găsește un cabinet stomatologic',
        subtitle: 'Caută după nume de cabinet, medic, oraș sau tratament și programează-te online.',
        searchPlaceholder: 'Cabinet, medic sau cuvânt cheie',
        cityPlaceholder: 'Oraș',
        treatmentPlaceholder: 'Tratament (ex. implant)',
        search: 'Caută',
        searching: 'Se caută…',
        error: 'Cabinetele nu au putut fi încărcate. Încearcă din nou.',
        none: 'Niciun cabinet nu corespunde căutării.',
        doctors: 'Medici',
        viewClinic: 'Vezi cabinetul →',
        unitOne: 'cabinet',
        unitMany: 'cabinete',
        loadMore: 'Încarcă mai multe',
        searchArea: 'Caută în această zonă',
      },
      clinic: {
        directory: '← Director',
        bookOnline: 'Programează online',
        doctors: 'Medici',
        services: 'Servicii',
        hours: 'Program',
        contact: 'Contact',
        notFound: 'Cabinet negăsit',
        days: ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'],
      },
      booking: {
        bookOnline: 'Programează online',
        date: 'Data',
        availableTimes: 'Ore disponibile',
        loadingTimes: 'Se încarcă orele…',
        noSlots: 'Nicio oră liberă în această zi — încearcă altă dată.',
        firstName: 'Prenume',
        lastName: 'Nume',
        phone: 'Telefon (+40712345678)',
        email: 'Email (opțional)',
        depositNote: 'Se va solicita un avans de {{amount}} pentru a confirma programarea (plătit online după rezervare).',
        consent: 'Sunt de acord ca {{clinic}} să îmi prelucreze datele personale pentru gestionarea acestei programări și pentru a mă contacta în legătură cu ea (GDPR).',
        pickTime: 'Alege o oră',
        booking: 'Se rezervă…',
        bookAt: 'Rezervă {{time}}',
        slotTaken: 'Ora tocmai a fost ocupată. Te rugăm să alegi alta.',
        failed: 'Programarea nu a putut fi finalizată. Verifică datele și încearcă din nou.',
        requested: 'Programare solicitată',
        sent: 'Am trimis cererea către cabinet. Vei primi în curând o confirmare.',
        depositSecures: 'Un avans de {{amount}} confirmă programarea ta.',
        payDeposit: 'Plătește avansul de {{amount}} →',
        backTo: '← Înapoi la {{clinic}}',
      },
      forClinics: {
        allFeatures: 'Vezi toate funcționalitățile',
        featMore: 'Cum funcționează',
        title: 'easyDent pentru cabinete',
        subtitle: 'Platforma cloud care îți gestionează cabinetul stomatologic — programări, fișe pacienți, harta dentară, plăți și comunicări, toate într-un singur loc.',
        heroTitle: 'Sistemul de operare al cabinetului tău stomatologic',
        heroSubtitle: 'easyDent aduce programările, fișele pacienților, harta dentară, plățile și comunicarea cu pacienții într-o singură platformă cloud — plus un director public care îți trimite pacienți noi.',
        heroCta: 'Începe proba gratuită',
        heroDemo: 'Explorează demo-ul live',
        heroNote: 'Probă gratuită de 14 zile · fără card bancar',
        valueTitle: 'Tot ce ține cabinetul în mișcare',
        featSchedT: 'Programări inteligente', featSchedD: 'Umpli fiecare scaun și afli care dintre pacienții de mâine riscă să nu vină.',
        featChartT: 'Fișe clinice', featChartD: 'Schemă, notițe, radiografii și nota de plată — toate din notarea vizitei o singură dată.',
        featPayT: 'Plăți', featPayD: 'Încasezi la recepție sau prin link — și ceri un avans, ca neprezentarea să îl coste și pe el.',
        featCommsT: 'Comunicare cu pacienții', featCommsD: 'Reamintiri care pleacă singure, ca programările uitate să nu îți mai golească scaunele.',
        featAiT: 'Asistent AI', featAiD: 'Dictezi notițele în loc să le tastezi și petreci programarea uitându-te la pacient.',
        featDirT: 'Fii găsit', featDirD: 'Pacienți noi te găsesc și se programează singuri — la miezul nopții, dacă atunci caută.',
        pricingTitle: 'Prețuri simple și transparente',
        pricingSubtitle: 'Fiecare plan include o probă gratuită de 14 zile. Economisești 17% cu plata anuală.',
        perMonth: '/lună',
        popular: 'Cel mai popular',
        planCta: 'Începe proba gratuită',
        onboardTitle: 'Funcțional într-o zi',
        questions: 'Nu ești sigur că se potrivește cabinetului tău?',
        onbT1: 'Începe proba', onbD1: 'Creează-ți cabinetul — fără card, fără instalare.',
        onbT2: 'Configurează', onbD2: 'Adaugă scaune, medici, servicii și programul de lucru.',
        onbT3: 'Pornește', onbD3: 'Primește programări online și începe să vezi pacienți.',
        plans: {
          starter: { name: 'Starter', price: '€89', blurb: 'Pentru un cabinet la început de drum.', features: ['O locație', 'Până la 3 scaune', 'Calendar, pacienți și programări', 'Reamintiri SMS și email', 'Programare online + listare în director', '5 GB stocare'] },
          pro: { name: 'Professional', price: '€179', blurb: 'Pentru cabinete în creștere.', features: ['Până la 3 locații', 'Până la 10 scaune', 'Tot ce e în Starter', 'Hartă dentară și fișe clinice', 'Plăți și avansuri', 'Dictare AI și predicția neprezentărilor', '25 GB stocare'] },
          clinic: { name: 'Clinic', price: '€349', blurb: 'Pentru clinici cu mai multe locații.', features: ['Locații și scaune nelimitate', 'Tot ce e în Professional', 'Inventar și stoc', 'Rapoarte și analize', 'Chat intern și acces API', '100 GB stocare', 'Suport prioritar'] },
        },
        percentOff: '{{percent}}% reducere',
        promoUntil: 'până pe {{date}}',
        tryDemo: 'Încearcă demo-ul live',
        tryDemoText: 'Explorează un cabinet demo complet populat — fără cont. Alege un rol pentru a intra:',
        enterDemo: 'Intră în demo →',
        staffLabel: 'Personal cabinet',
        patientLabel: 'Portal pacient',
        patientDesc: 'Vezi cabinetul din perspectiva pacientului',
        signinManually: 'Sau autentifică-te manual la',
        withAny: 'cu oricare din:',
        password: 'Parolă',
        sharedSandbox: 'Este un mediu de test partajat — și alții pot explora, iar datele se resetează zilnic.',
        backToDirectory: '← Înapoi la directorul de cabinete',
      },
    },
  },
  en: {
    translation: {
      demo: {
        roles: {
          admin: "Administrator",
          doctor: "Doctor",
          receptionist: "Receptionist",
          adminDesc: "Full clinic management",
          doctorDesc: "Clinical & calendar",
          receptionistDesc: "Front desk & scheduling",
        },
      },
      features: featuresEn,
      lang: { ro: 'RO', en: 'EN' },
      common: {
        language: 'Language',
        theme: { label: 'Theme', light: 'Light', dark: 'Dark', system: 'System' },
        back: 'Back', loading: 'Loading…', backToDirectory: '← Back to the directory',
        cookieBanner: {
          dialogLabel: 'Cookie consent',
          intro: 'We use strictly necessary cookies to make this work. With your agreement we also use analytics and marketing cookies.',
          policyLink: 'Cookie policy',
          necessary: 'Strictly necessary (always on)',
          analytics: 'Analytics',
          marketing: 'Marketing',
          acceptAll: 'Accept all',
          necessaryOnly: 'Necessary only',
          customise: 'Customise',
          saveChoice: 'Save choice',
        },
      },
      imaging: {
        title: 'For dental imaging centres',
        subtitle: 'Take referrals from the clinics you already work with, and send the images back into their patient records.',
        step1: 'A referral arrives',
        step1Body: 'A dentist sends a patient to you and the request lands in your worklist — who is coming, which views, which teeth, and why.',
        step2: 'You do the imaging',
        step2Body: 'Accept it, or decline with a reason. If the patient never turns up, say so — the clinic needs to know.',
        step3: 'The study goes straight back',
        step3Body: 'Upload the images and they appear on the patient’s record and on the consultation the dentist raised them from. No CDs, no WeTransfer.',
        priceTitle: 'Free to join',
        priceBody: 'There is no charge for receiving referrals or delivering studies. If that ever changes, you will be told well before it does.',
        howToJoin: 'Accounts are set up by easyDent so we can verify a centre before any patient data reaches it. Email us and we will get you on.',
        signIn: 'Sign in',
        tryDemo: 'Open the demo',
      },
      patients: {
        title: 'For patients',
        subtitle: 'Your dental history belongs to you. The portal is free, and your clinic invites you to it.',
        recordsTitle: 'Your records, wherever you were treated',
        recordsBody: 'Treatments, dental chart, X-rays and documents from every clinic you have linked — in one place, and yours to download whenever you want.',
        bookingTitle: 'Book without phoning',
        bookingBody: 'See free slots at your clinic and book, move or cancel an appointment yourself. Reminders come by the channel you choose.',
        payTitle: 'Pay and keep the receipts',
        payBody: 'Settle what you owe from your phone and keep every receipt and invoice in one list.',
        controlTitle: 'You decide who sees what',
        controlBody: 'Linking a clinic does not hand it your history from other clinics — sharing is a separate choice, and you can take it back at any time. Nothing is sold to anyone, ever.',
        howToJoin: 'No sign-up form: your clinic sends you an invite link, so an account only ever exists for someone who is actually their patient.',
        findClinic: 'Find a clinic',
        signIn: 'Sign in',
        tryDemo: 'Open the demo',
      },
      suppliers: {
        title: 'For dental suppliers',
        subtitle: 'Reach dentists and their patients inside the software the clinic already has open — not in an inbox.',
        step1: 'Upload your creative',
        step1Body: 'An image and a link. We review it before it goes anywhere — clinics see approved creatives only.',
        step2: 'Choose who sees it',
        step2Body: 'Clinic staff inside the app they work in all day, or patients in their portal. Set a budget and a date range.',
        step3: 'Pay for what was actually seen',
        step3Body: 'An impression counts only after a full second on screen. Spend comes off a pre-funded balance, so there is no invoice to chase and no way to overspend.',
        priceTitle: '€6 per thousand impressions',
        priceBody: 'Billed from a balance you top up in advance. Nothing runs when the balance is empty, and you can stop a campaign at any time.',
        privacyTitle: 'What you do not get',
        privacyBody: 'No patient data, ever. You see impressions and clicks in aggregate — never who saw your ad. Patient-facing placements additionally require that patient’s marketing consent.',
        howToJoin: 'Accounts are set up by easyDent so we can verify a supplier before anything of theirs is shown to a clinic. Email us and we will get you on.',
        signIn: 'Sign in',
        tryDemo: 'Open the demo',
      },
      home: {
        nav: { findDentist: 'Find a dentist', forClinics: 'For clinics', features: 'Features', signIn: 'Sign in', staff: 'Clinic staff', patient: 'Patient', menu: 'Menu' },
        hero: {
          title: 'Find a dentist and book online',
          subtitle: 'Search dental clinics near you, compare services, and book an appointment in seconds — no account needed.',
          keyword: 'Clinic, doctor or treatment',
          city: 'City',
          search: 'Search',
          browseAll: 'Browse all clinics →',
          trust: 'Free for patients · No sign-up · GDPR-compliant',
        },
        steps: {
          title: 'Book in three steps',
          s1t: 'Search', s1d: 'Find clinics near you by name, city or treatment.',
          s2t: 'Choose', s2d: 'Compare doctors, services and opening hours.',
          s3t: 'Book', s3d: 'Pick a time and confirm online — you’ll get a reminder.',
        },
        features: {
          booking: 'Online booking 24/7', bookingD: 'Reserve a slot any time, day or night.',
          reminders: 'Automatic reminders', remindersD: 'SMS, WhatsApp or email so you never miss it.',
          records: 'Your records, online', recordsD: 'Appointments, history and payments in one place.',
          privacy: 'Private by design', privacyD: 'Your data is encrypted and GDPR-protected.',
        },
        clinics: {
          kicker: 'For dental clinics',
          title: 'Run your whole clinic on easyDent',
          subtitle: 'Scheduling, patient records, the dental chart, payments and patient messaging — one cloud platform, no installs.',
          f1: 'Smart calendar with no-show prediction',
          f2: 'Digital dental chart & clinical records',
          f3: 'Online payments and automated reminders',
          f4: 'Your clinic listed in the patient directory',
          cta: 'Start your 14-day free trial',
          ctaNote: 'No credit card required.',
          learn: 'See how it works →',
        },
        demo: {
          title: 'Just exploring?',
          text: 'Step into a fully-populated demo clinic — no signup. Data resets daily.',
          staff: 'Clinic staff', patient: 'Patient portal', enter: 'Enter demo →',
          note: 'Shared sandbox for evaluation.',
            imaging: 'Imaging centre',
            imagingDesc: 'Referrals in, studies back',
            patientDesc: 'Appointments, records, payments',
            supplier: 'Supplier',
            supplierDesc: 'Campaigns, creatives, spend',
        },
        footer: {
          features: 'All features',
          tagline: 'The dental practice platform.',
          patients: 'Patients', clinics: 'Clinics', account: 'Sign in',
          findDentist: 'Find a dentist', forClinics: 'For clinics', freeTrial: 'Free trial',
          clinicSignin: 'Clinic sign-in', patientSignin: 'Patient sign-in',
          rights: '© 2026 easyDent. All rights reserved.',
          legal: 'Legal',
        },
      },
      contact: {
        title: 'Contact us',
        seoDescription: 'Get in touch about easyDent — clinics, suppliers, imaging centres and patients.',
        intro: 'Send us a message and we\'ll get back to you, usually within one working day. If you already have a clinic account, raising a support request from inside the app reaches the right team faster.',
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        optional: '(optional)',
        topic: 'What\'s this about',
        topics: {
          clinics: 'I run a dental practice',
          suppliers: 'I\'m a supplier',
          imaging: 'I run an imaging centre',
          patients: 'I\'m a patient',
          billing: 'Billing and subscription',
          other: 'Something else',
        },
        message: 'Message',
        messagePlaceholder: 'Tell us how we can help.',
        privacy: 'We use the details above only to reply to you. This message does not sign you up for marketing.',
        submit: 'Send message',
        sending: 'Sending…',
        doneTitle: 'Your message reached us',
        doneBody: 'We\'ve emailed you a confirmation too. We\'ll reply shortly — if you\'d like to add anything, write to us at contact@easydent365.com.',
        error: 'Your message could not be sent. Please try again in a moment.',
        rateLimited: 'We\'ve had a few messages from you already. Please wait a little before sending another.',
      },
      legal: {
        title: 'Legal',
        seoDescription: 'Terms of service, privacy policy and cookie policy for easyDent.',
        notFound: 'That document does not exist.',
        loadError: 'This document could not be loaded. Please try again shortly.',
        draft: 'Draft — pending legal review.',
        version: 'Version {{version}} · effective {{date}}',
        doc: {
          terms: 'Terms of service',
          privacy: 'Privacy policy',
          cookies: 'Cookie policy',
          dpa: 'Data processing agreement',
          'patient-privacy': 'Patient privacy notice',
          'sub-processors': 'Sub-processors',
        },
      },
      landing: {
        tagline: 'The dental practice platform — pick an app to open.',
        findDentistTitle: 'Looking for a dentist?',
        findDentistText: 'Search dental clinics near you on the map and book your appointment online.',
        findClinic: 'Find a clinic →',
        demoTitle: 'Jump into the demo clinic',
        demoText: 'A fully-populated sandbox — no signup. Pick a role to sign in to the clinic app:',
        enterDemo: 'Enter demo →',
        staffLabel: 'Clinic staff',
        patientLabel: 'Patient portal',
        patientDesc: 'Appointments, medical history and payments',
        manualSignin: 'Manual sign-in uses the same emails with password',
        sharedSandbox: 'Shared sandbox — data resets daily.',
        applications: 'Applications',
        comingSoon: 'Coming soon',
        open: 'Open →',
      },
      directory: {
        title: 'Find a dental clinic',
        subtitle: 'Search by clinic name, doctor, city, or treatment and book online.',
        searchPlaceholder: 'Clinic, doctor, or keyword',
        cityPlaceholder: 'City',
        treatmentPlaceholder: 'Treatment (e.g. implant)',
        search: 'Search',
        searching: 'Searching…',
        error: 'Could not load clinics. Please try again.',
        none: 'No clinics match your search.',
        doctors: 'Doctors',
        viewClinic: 'View clinic →',
        unitOne: 'clinic',
        unitMany: 'clinics',
        loadMore: 'Load more',
        searchArea: 'Search this area',
      },
      clinic: {
        directory: '← Directory',
        bookOnline: 'Book online',
        doctors: 'Doctors',
        services: 'Services',
        hours: 'Opening hours',
        contact: 'Contact',
        notFound: 'Clinic not found',
        days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      },
      booking: {
        bookOnline: 'Book online',
        date: 'Date',
        availableTimes: 'Available times',
        loadingTimes: 'Loading times…',
        noSlots: 'No free slots on this day — try another date.',
        firstName: 'First name',
        lastName: 'Last name',
        phone: 'Phone (+40712345678)',
        email: 'Email (optional)',
        depositNote: 'A {{amount}} deposit will be requested to secure your booking (paid online after you book).',
        consent: 'I agree to {{clinic}} processing my personal data to manage this appointment and contact me about it (GDPR).',
        pickTime: 'Pick a time',
        booking: 'Booking…',
        bookAt: 'Book {{time}}',
        slotTaken: 'That time was just taken. Please pick another slot.',
        failed: 'Could not complete the booking. Check your details and try again.',
        requested: 'Appointment requested',
        sent: 'We’ve sent your request to the clinic. You’ll get a confirmation shortly.',
        depositSecures: 'A {{amount}} deposit secures your booking.',
        payDeposit: 'Pay {{amount}} deposit →',
        backTo: '← Back to {{clinic}}',
      },
      forClinics: {
        allFeatures: 'See all features',
        featMore: 'How it works',
        title: 'easyDent for clinics',
        subtitle: 'The cloud platform that runs your dental clinic — scheduling, patient records, the dental chart, payments and communications, all in one place.',
        heroTitle: 'The operating system for your dental clinic',
        heroSubtitle: 'easyDent brings scheduling, patient records, the dental chart, payments and patient messaging into one cloud platform — with a public directory that sends you new patients.',
        heroCta: 'Start your free trial',
        heroDemo: 'Explore the live demo',
        heroNote: '14-day free trial · no credit card',
        valueTitle: 'Everything your practice runs on',
        featSchedT: 'Smart scheduling', featSchedD: 'Fill every chair, and know which of tomorrow’s patients are about to not turn up.',
        featChartT: 'Clinical records', featChartD: 'Chart, notes, X-rays and the bill — all from recording the visit once.',
        featPayT: 'Payments', featPayD: 'Get paid at the desk or by link — and take a deposit so a no-show costs them too.',
        featCommsT: 'Patient messaging', featCommsD: 'Reminders that go out by themselves, so forgotten appointments stop emptying your chairs.',
        featAiT: 'AI assistant', featAiD: 'Dictate your notes instead of typing them, and spend the appointment looking at the patient.',
        featDirT: 'Get discovered', featDirD: 'New patients find you and book themselves in — at midnight, if that is when they are looking.',
        pricingTitle: 'Simple, transparent pricing',
        pricingSubtitle: 'Every plan includes a 14-day free trial. Save 17% with annual billing.',
        perMonth: '/mo',
        popular: 'Most popular',
        planCta: 'Start free trial',
        onboardTitle: 'Live in a day',
        questions: 'Not sure it fits your practice?',
        onbT1: 'Start your trial', onbD1: 'Create your clinic — no card, no install.',
        onbT2: 'Set up', onbD2: 'Add chairs, doctors, services and your working hours.',
        onbT3: 'Go live', onbD3: 'Take bookings online and start seeing patients.',
        plans: {
          starter: { name: 'Starter', price: '€89', blurb: 'For a single practice getting started.', features: ['1 clinic location', 'Up to 3 chairs', 'Calendar, patients & appointments', 'SMS & email reminders', 'Online booking + directory listing', '5 GB storage'] },
          pro: { name: 'Professional', price: '€179', blurb: 'For growing practices.', features: ['Up to 3 locations', 'Up to 10 chairs', 'Everything in Starter', 'Dental chart & clinical records', 'Payments & deposits', 'AI dictation & no-show prediction', '25 GB storage'] },
          clinic: { name: 'Clinic', price: '€349', blurb: 'For multi-site clinics.', features: ['Unlimited locations & chairs', 'Everything in Professional', 'Inventory & stock', 'Reports & analytics', 'Staff chat & API access', '100 GB storage', 'Priority support'] },
        },
        percentOff: '{{percent}}% off',
        promoUntil: 'until {{date}}',
        tryDemo: 'Try the live demo',
        tryDemoText: 'Explore a fully-populated demo clinic — no signup. Pick a role to enter:',
        enterDemo: 'Enter demo →',
        staffLabel: 'Clinic staff',
        patientLabel: 'Patient portal',
        patientDesc: 'See the clinic from the patient’s side',
        signinManually: 'Or sign in manually at',
        withAny: 'with any of:',
        password: 'Password',
        sharedSandbox: 'This is a shared sandbox — others may be exploring too, and all data resets every day.',
        backToDirectory: '← Back to the clinic directory',
      },
    },
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: saved === 'en' ? 'en' : 'ro',
  fallbackLng: 'ro',
  supportedLngs: ['ro', 'en'],
  interpolation: { escapeValue: false },
  returnObjects: true,
});

// If we arrived carrying a language from another app, make it sticky locally too.
if (shared) persistSharedLang(shared);

export const setLang = (lng: 'ro' | 'en') => {
  i18n.changeLanguage(lng);
  persistSharedLang(lng); // localStorage + shared parent-domain cookie so other apps inherit it
};

export default i18n;
